import { DotsHorizontalIcon } from '@radix-ui/react-icons'
import { useQueryClient } from '@tanstack/react-query'
import { Row } from '@tanstack/react-table'
import {
  IconChartBar,
  IconCopy,
  IconPower,
  IconTrash,
} from '@tabler/icons-react'
import { listAutomationsQuery } from '@/queries/automation.query'
import { AutomationService } from '@/services/automations.service'
import { toast } from 'sonner'
import { useFilters } from '@/hooks/useFilters'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAutomations } from '../context/automations-context'
import { automationSchema } from '../schema'

interface DataTableRowActionsProps<TData> {
  row: Row<TData>
}

export function DataTableRowActions<TData>({
  row,
}: DataTableRowActionsProps<TData>) {
  const automation = automationSchema.parse(row.original)
  const { setOpen, setCurrentRow } = useAutomations()
  const queryClient = useQueryClient()
  const { filters } = useFilters('/_authenticated/automations/')

  const handleToggle = async () => {
    const newStatus = !automation.isActive
    const action = newStatus ? 'ativada' : 'desativada'

    toast.promise(
      AutomationService.toggle(automation.id, { isActive: newStatus }),
      {
        loading: `${newStatus ? 'Ativando' : 'Desativando'} automação...`,
        success: () => {
          queryClient.invalidateQueries(listAutomationsQuery(filters))
          return `Automação ${action} com sucesso!`
        },
        error: `Erro ao ${newStatus ? 'ativar' : 'desativar'} automação`,
      }
    )
  }

  const handleDuplicate = async () => {
    toast.promise(AutomationService.duplicate(automation.id), {
      loading: 'Duplicando automação...',
      success: () => {
        queryClient.invalidateQueries(listAutomationsQuery())
        return 'Automação duplicada com sucesso!'
      },
      error: 'Erro ao duplicar automação',
    })
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          className='flex h-8 w-8 p-0 data-[state=open]:bg-muted'
        >
          <DotsHorizontalIcon className='h-4 w-4' />
          <span className='sr-only'>Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-[180px]'>
        <DropdownMenuItem
          onClick={() => {
            setCurrentRow(automation)
            setOpen('update')
          }}
        >
          Editar
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleDuplicate}>
          Duplicar
          <DropdownMenuShortcut>
            <IconCopy size={16} />
          </DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleToggle}>
          {automation.isActive ? 'Desativar' : 'Ativar'}
          <DropdownMenuShortcut>
            <IconPower size={16} />
          </DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            setCurrentRow(automation)
            setOpen('stats')
          }}
        >
          Estatísticas
          <DropdownMenuShortcut>
            <IconChartBar size={16} />
          </DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            setCurrentRow(automation)
            setOpen('delete')
          }}
        >
          Excluir
          <DropdownMenuShortcut>
            <IconTrash size={16} />
          </DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
